import { useState, useRef, useEffect } from 'react'
import { apiClient } from '@/api/client'
import { Button } from '@/components/ui/button'
import { Card, CardHeader, CardTitle, CardDescription, CardContent, CardFooter } from '@/components/ui/card'
import { Label } from '@/components/ui/label'

interface PortalTermsGateProps {
  portalToken: string
  role: 'seller' | 'buyer'
  onAcceptTos: () => void
}

export function PortalTermsGate({ portalToken, role, onAcceptTos }: PortalTermsGateProps) {
  const [tosChecked, setTosChecked] = useState(false)
  const [privacyChecked, setPrivacyChecked] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const checkboxRef = useRef<HTMLInputElement>(null)

  const canContinue = tosChecked && privacyChecked && !submitting

  useEffect(() => {
    checkboxRef.current?.focus()
  }, [])

  const handleAccept = async () => {
    if (!canContinue) return
    setSubmitting(true)
    setError(null)
    try {
      const endpoint = role === 'seller'
        ? `/qoe/seller/${portalToken}/accept-tos`
        : `/qoe/buyer/${portalToken}/accept-tos`
      await apiClient.post(endpoint, { tos_accepted: true })
      onAcceptTos()
    } catch {
      setError('Failed to record Terms acceptance. Please try again.')
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-pal-overlay/80 p-4" role="presentation">
      <Card
        className="w-full max-w-2xl"
        role="dialog"
        aria-modal="true"
        aria-labelledby="portal-tos-title"
        aria-describedby="portal-tos-description"
      >
        <CardHeader className="space-y-3 border-b">
          <CardTitle id="portal-tos-title" className="text-2xl">
            Terms of Service & Privacy Policy
          </CardTitle>
          <CardDescription id="portal-tos-description" className="text-base">
            Please review and accept these terms to access the portal.
          </CardDescription>
        </CardHeader>
        <CardContent className="max-h-96 overflow-y-auto space-y-6 py-6">
          <div className="space-y-3 text-sm leading-relaxed">
            <p>
              By accessing this portal, you acknowledge that you have reviewed and agree to the{' '}
              <a href="/terms" target="_blank" rel="noopener noreferrer" className="font-medium text-primary hover:underline">
                Terms of Service
              </a>
              {' '}and{' '}
              <a href="/privacy" target="_blank" rel="noopener noreferrer" className="font-medium text-primary hover:underline">
                Privacy Policy
              </a>.
            </p>
          </div>
          <div className="space-y-3">
            <Label className="flex items-start gap-3 cursor-pointer p-3 rounded-lg hover:bg-accent transition-colors">
              <input
                ref={checkboxRef}
                type="checkbox"
                className="mt-1 size-4 shrink-0 rounded border-input cursor-pointer"
                checked={tosChecked}
                onChange={e => setTosChecked(e.target.checked)}
                aria-label="I have read and agree to the Terms of Service"
              />
              <span className="font-normal text-sm">
                I have read and agree to the Terms of Service.
              </span>
            </Label>
            <Label className="flex items-start gap-3 cursor-pointer p-3 rounded-lg hover:bg-accent transition-colors">
              <input
                type="checkbox"
                className="mt-1 size-4 shrink-0 rounded border-input cursor-pointer"
                checked={privacyChecked}
                onChange={e => setPrivacyChecked(e.target.checked)}
                aria-label="I have read and agree to the Privacy Policy"
              />
              <span className="font-normal text-sm">
                I have read and agree to the Privacy Policy.
              </span>
            </Label>
          </div>
          {error && (
            <p className="text-sm text-destructive bg-destructive/10 p-3 rounded">
              {error}
            </p>
          )}
        </CardContent>
        <CardFooter className="border-t justify-end gap-3 pt-6">
          <Button
            variant="outline"
            disabled={submitting}
            onClick={() => window.history.back()}
          >
            Back
          </Button>
          <Button
            disabled={!canContinue}
            onClick={handleAccept}
            className="min-w-32"
          >
            {submitting ? 'Accepting…' : 'Accept & Continue'}
          </Button>
        </CardFooter>
      </Card>
    </div>
  )
}
